import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  IconButton,
  Button,
} from '@mui/material';
import { ChevronRight, ExpandMore, Search as SearchIcon, FileDownload as DownloadIcon } from '@mui/icons-material';
import { getTechnologyHierarchy, TechnologyHierarchyNode, TechnologyHierarchyResponse } from '../../api/technologies';
import { useNotification } from '../../context/NotificationContext';

const TechnologyMapPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [hierarchyData, setHierarchyData] = useState<TechnologyHierarchyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);

  const handleExport = () => {
    if (!hierarchyData) return;

    const flattenNodes = (nodes: TechnologyHierarchyNode[], parentId: string = '', parentName: string = ''): any[] => {
      let result: any[] = [];
      nodes.forEach(node => {
        const isTerminal = !node.subtypes || node.subtypes.length === 0;
        result.push({
          id: node.id,
          name: node.name,
          level: node.level,
          parentId,
          parentName,
          isTerminal: isTerminal ? 'Yes' : 'No'
        });
        if (node.subtypes && node.subtypes.length > 0) {
          result = [...result, ...flattenNodes(node.subtypes, node.id, node.name)];
        }
      });
      return result;
    };

    const flatData = flattenNodes(hierarchyData.technologies);
    const headers = ['Level', 'ID', 'Name', 'Parent ID', 'Parent Name', 'Is Terminal'];

    const escapeCSV = (val: any) => {
      const stringVal = val === null || val === undefined ? '' : String(val);
      if (stringVal.includes(',') || stringVal.includes('"') || stringVal.includes('\n')) {
        return `"${stringVal.replace(/"/g, '""')}"`;
      }
      return stringVal;
    };

    const csvContent = [
      headers.join(','),
      ...flatData.map(item => [
        item.level,
        escapeCSV(item.id),
        escapeCSV(item.name),
        escapeCSV(item.parentId),
        escapeCSV(item.parentName),
        escapeCSV(item.isTerminal)
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `technology_map_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showNotification('Technology map exported successfully', 'success');
  };

  useEffect(() => {
    const fetchHierarchy = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getTechnologyHierarchy();
        setHierarchyData(data);
      } catch (err: any) {
        setError('Failed to load technology hierarchy. Please try again.');
        console.error('Error fetching technology hierarchy:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHierarchy();
  }, []);

  const handleNodeClick = (technologyId: string) => {
    navigate(`/technologies?id=${technologyId}`);
  };

  const filterNodes = (nodes: TechnologyHierarchyNode[], query: string): TechnologyHierarchyNode[] => {
    if (!query.trim()) return nodes;

    const lowerQuery = query.toLowerCase();
    return nodes.reduce<TechnologyHierarchyNode[]>((acc, node) => {
      const matchesSearch = node.name.toLowerCase().includes(lowerQuery);
      const filteredSubtypes = filterNodes(node.subtypes, query);

      if (matchesSearch || filteredSubtypes.length > 0) {
        acc.push({
          ...node,
          subtypes: filteredSubtypes,
        });
      }

      return acc;
    }, []);
  };

  const expandNodesWithMatches = (nodes: TechnologyHierarchyNode[], query: string): string[] => {
    if (!query.trim()) return [];

    const expandedIds: string[] = [];
    const lowerQuery = query.toLowerCase();

    const traverse = (node: TechnologyHierarchyNode) => {
      if (node.name.toLowerCase().includes(lowerQuery) || node.subtypes.some(subtype => subtype.name.toLowerCase().includes(lowerQuery))) {
        expandedIds.push(node.id);
      }

      node.subtypes.forEach(traverse);
    };

    nodes.forEach(traverse);
    return expandedIds;
  };

  useEffect(() => {
    if (hierarchyData && searchQuery.trim()) {
      const expandedIds = expandNodesWithMatches(hierarchyData.technologies, searchQuery);
      setExpanded(expandedIds);
    } else if (!searchQuery.trim()) {
      setExpanded([]);
    }
  }, [searchQuery, hierarchyData]);

  const TreeNode: React.FC<{ node: TechnologyHierarchyNode; level?: number }> = ({ node, level = 0 }) => {
    const isOpen = expanded.includes(node.id);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      const newExpanded = isOpen
        ? expanded.filter(id => id !== node.id)
        : [...expanded, node.id];
      setExpanded(newExpanded);
    };

    const hasChildren = node.subtypes && node.subtypes.length > 0;

    return (
      <>
        <ListItem
          disablePadding
          sx={{ pl: level * 2 }}
        >
          <ListItemButton
            onClick={() => handleNodeClick(node.id)}
            sx={{
              py: 0.5,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            {hasChildren && (
              <IconButton
                size="small"
                onClick={handleToggle}
                sx={{ mr: 1, p: 0.5 }}
              >
                {isOpen ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
              </IconButton>
            )}
            {!hasChildren && <Box sx={{ width: 32 }} />}
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: hasChildren ? 600 : 400 }}>
                    {node.name}
                  </Typography>
                  {hasChildren && (
                    <Chip
                      label={node.subtypes.length}
                      size="small"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
              }
            />
          </ListItemButton>
        </ListItem>
        {hasChildren && (
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {node.subtypes.map((childNode) => (
                <TreeNode key={childNode.id} node={childNode} level={level + 1} />
              ))}
            </List>
          </Collapse>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!hierarchyData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">No technology hierarchy data available.</Alert>
      </Box>
    );
  }

  const filteredTechnologies = filterNodes(hierarchyData.technologies, searchQuery);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Technology Map
        </Typography>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          sx={{ borderRadius: 2 }}
        >
          Export CSV
        </Button>
      </Box>

      {/* Statistics */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Chip
          label={`Total: ${hierarchyData.statistics.total_unique_technologies}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`Terminal: ${hierarchyData.statistics.total_terminal_technologies}`}
          color="secondary"
          variant="outlined"
        />
        {Object.entries(hierarchyData.statistics.technologies_by_level).map(([level, stats]) => (
          <Chip
            key={level}
            label={`${level.replace('level_', 'L')}: ${stats.total}`}
            variant="outlined"
            size="small"
          />
        ))}
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Search technologies..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      {/* Tree View */}
      <Paper sx={{ p: 2, maxHeight: 'calc(100vh - 350px)', overflow: 'auto' }}>
        {filteredTechnologies.length > 0 ? (
          <List component="nav" disablePadding>
            {filteredTechnologies.map((node) => (
              <TreeNode key={node.id} node={node} />
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No technologies match your search.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default TechnologyMapPage;
